import i18n from "src/i18n/config";

import { FieldSpec, PolicyRule, RuleValues } from "./types";

const formatValue = (field: FieldSpec, value: RuleValues[string]): string => {
    const locale = i18n.resolvedLanguage;

    switch (field.type) {
        case "boolean":
            return value ? i18n.t("common:label.enabled") : i18n.t("common:label.disabled");
        case "numeric":
            return new Intl.NumberFormat(locale).format(Number(value));
        case "text":
        case "file":
            return String(value);
        case "list": {
            const entries = Array.isArray(value) ? value : [];
            return entries.length
                ? new Intl.ListFormat(locale, { type: "conjunction" }).format(entries)
                : i18n.t("common:label.none");
        }
        case "select": {
            const selected = Array.isArray(value) ? value : [String(value)];
            if (field.allSelectedLabel && field.options.every((option) => selected.includes(option.value))) {
                return field.allSelectedLabel();
            }
            if (selected.length === 0) {
                return i18n.t("common:label.none");
            }
            const labels = selected.map((item) => field.options.find((option) => option.value === item)?.label() ?? item);
            return new Intl.ListFormat(locale, { type: "conjunction" }).format(labels);
        }
    }
};

/**
 * Describes a configured rule as a sentence, e.g. "Rotate the screen to Portrait".
 * Returns null when the rule has no summary, so the caller falls back to its title.
 */
export function getRuleSummary(rule: PolicyRule, values: RuleValues): string | null {
    if (!rule.summary) {
        return null;
    }

    const displayed = rule.fields.filter((field) => field.name in values && !(field.type === "text" && field.secret));
    const formatted = Object.fromEntries(displayed.map((field) => [field.name, formatValue(field, values[field.name])]));

    return rule.summary(formatted, values);
}
